import { Dialog, Notify } from 'quasar'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import FormDialog from '@/components/common/FormDialog.vue'

/**
 * 对话原语薄门面（原生弹窗治理 Phase 1，2026-09-25）
 *
 * 为什么是「门面」而不是「框架」（审计 §4.5 / §7.6）：
 *   - 无注册表、无队列、无主题引擎、无插槽透传，**不接受任意 HTML / 任意组件**；
 *   - B 与 C 的差别只在本文件**一处**判定（confirmAction / confirmDanger），
 *     颜色、常驻策略、按钮措辞不散落到各调用点，杜绝「普通确认滥用红色」；
 *   - D 类输入**不在这里**偷换成一个字符串输入框，一律走 showForm 的字段规格（审计 §3.6 / §7.7）。
 *
 * 落点选择（审计 §4.1 已按 Quasar 1.22.10 源码盘点）：
 *   - `Dialog.create({ title, message, cancel })` 内置 message 模式**装不下** B/C/D：
 *     它没有 promise onOk（DialogPlugin.onOk 里 emit ok 后立即 hide）、没有 loading、
 *     失败无法留在弹窗里、ok 键恒为主题色（无危险/注意变体）、更没有输入字段。
 *     所以 B/C/D 走 `Dialog.create({ component })` 自定义组件——与仓内
 *     utils/pickupConfirm.js 同一机制、同一「三件套」契约（见 ConfirmDialog.vue 文件头）。
 *   - A 类（纯告知，占全仓命中 81%）走 Notify，不做决策、无返回值（审计 §2.3 / §4.3）。
 *
 * 全部返回 Promise：原生 confirm/prompt 是同步阻塞的、弹窗是异步的，统一成 await 形态后
 * 调用点可 grep、可 lint（`await confirmAction(` / `await confirmDanger(` / `await showForm(`）。
 */

/** A 类默认时长：成功一闪而过，失败/警告要能读完（审计 §2.3） */
const NOTIFY_TIMEOUT = { positive: 1200, negative: 3000, warning: 3000, info: 2000 }
const NOTIFY_TYPES = ['positive', 'negative', 'warning', 'info']

/** B 类允许的语义色：primary=流程推进蓝，attention=仓内「不可逆但非破坏」的注意橙（Order.vue 改为自提即此色） */
const CONFIRM_SEMANTICS = ['primary', 'attention']

function reject (message) {
  throw new Error('[utils/dialog] ' + message)
}

/**
 * A 类：纯告知（成功 / 失败 / 校验提示 / 服务端 message 直出）。**不做决策**，因此没有返回值可言——
 * 需要用户 Yes/No 的走 confirmAction，需要用户填内容的走 showForm，二者不可用本函数凑。
 *
 * @param {String} text 文案（服务端 message 直接透传时请先自行兜底空串）
 * @param {Object} [opts] { type: 'positive'|'negative'|'warning'|'info', timeout, caption }
 * @returns {Promise<void>}
 */
export function showMessage (text, opts = {}) {
  const type = NOTIFY_TYPES.includes(opts.type) ? opts.type : 'info'
  Notify.create({
    type,
    message: String(text == null ? '' : text),
    timeout: opts.timeout == null ? NOTIFY_TIMEOUT[type] : opts.timeout,
    ...(opts.caption ? { caption: opts.caption } : {})
  })
  return Promise.resolve()
}

/**
 * 组装一次确认（B/C 共用的唯一实现，差别只在 semantic 与 persistent 默认值）。
 * @param {Object} opts 见 confirmAction / confirmDanger
 * @param {String} klass 'normal' | 'danger'
 */
function openConfirm (opts, klass) {
  // 签名即护栏：**不接受字符串**——`confirmAction('要删吗')` 就是用户明令禁止的「万能 confirm(text)」
  if (opts == null || typeof opts !== 'object' || Array.isArray(opts)) {
    reject('只接受选项对象（禁止万能 confirm(text)：标题/问句/后果必须显式给出）')
  }
  const isDanger = klass === 'danger'
  if (!opts.title || !String(opts.title).trim()) {
    reject('必须给出清晰标题 title（用户要在第一眼知道自己在确认什么）')
  }
  if (isDanger) {
    // 从签名上堵死「危险确认只问一句」：说不清后果，说明这处根本不该是红色
    if (!opts.consequence || !String(opts.consequence).trim()) {
      reject('confirmDanger() 必须给出 consequence（"将会发生什么"）；说不清后果的确认请用 confirmAction()')
    }
    if (opts.semantic != null && opts.semantic !== 'danger') {
      reject('confirmDanger() 的 semantic 恒为 danger，不可改判')
    }
  } else {
    if (opts.semantic === 'danger') {
      reject('普通确认不得使用 danger 红；危险操作请改用 confirmDanger()')
    }
    if (opts.semantic != null && !CONFIRM_SEMANTICS.includes(opts.semantic)) {
      reject('semantic 只接受 ' + CONFIRM_SEMANTICS.join(' / ') + '（危险色请用 confirmDanger）')
    }
  }

  return new Promise(resolve => {
    const dialog = Dialog.create({
      component: ConfirmDialog,
      parent: opts.parent,
      title: String(opts.title),
      message: opts.message ? String(opts.message) : '',
      consequence: opts.consequence ? String(opts.consequence) : '',
      okLabel: opts.okLabel ? String(opts.okLabel) : '确定',
      cancelLabel: opts.cancelLabel ? String(opts.cancelLabel) : '取消',
      semantic: isDanger ? 'danger' : (opts.semantic || 'primary'),
      // 常驻策略只在这里定：C 类**强制**常驻（防误点遮罩把危险确认点掉）；B 类默认允许点遮罩取消（审计 §4.2 第 7 条）
      persistent: isDanger ? true : opts.persistent === true,
      // 可选：把这一次请求也放进弹窗（异步期间 loading + 禁用防双击；失败留在弹窗里，不静默关掉）
      action: typeof opts.action === 'function' ? opts.action : null
    })
    dialog.onOk(() => resolve(true))
    dialog.onCancel(() => resolve(false))
  })
}

/**
 * B 类：普通确认（代价可逆 / 有回退路径）。白底主色键；`semantic: 'attention'` 用于仓内
 * 「不可逆但不破坏数据」的动作（如 Order.vue 的改为到店自提——仓内已定为橙，不得改判成红）。
 *
 * @param {Object} opts
 *   @param {String}  opts.title        必填，清晰标题
 *   @param {String} [opts.message]     问句正文
 *   @param {String} [opts.consequence] 可选的「将会发生什么」
 *   @param {String} [opts.okLabel]     确认键文案（默认「确定」）
 *   @param {String} [opts.cancelLabel] 取消键文案（默认「取消」）
 *   @param {String} [opts.semantic]    'primary'（默认）| 'attention'
 *   @param {Boolean}[opts.persistent]  true=点遮罩/ESC 不关（不可逆动作应传 true）
 *   @param {Function}[opts.action]     可选 async：在弹窗内执行；抛错则弹窗不关并显示原因
 *   @param {Vue}    [opts.parent]      调用方实例（继承主题/上下文，同 utils/pickupConfirm.js 约定）
 * @returns {Promise<Boolean>} true=确认，false=取消/关闭
 */
export function confirmAction (opts) {
  return openConfirm(opts, 'normal')
}

/**
 * C 类：危险确认（物理删除 / 他人账号权益 / 批量不可逆分发 / 不可逆业务态切换，
 * 判定清单见审计 §3.3 的 40 处）。恒为项目危险色 `--destructive`（非高饱和 --color-danger）、
 * 恒常驻、必须有 consequence。
 *
 * @param {Object} opts 同 confirmAction；`consequence` 变必填，`semantic` 不可指定
 * @returns {Promise<Boolean>} true=确认，false=取消/关闭
 */
export function confirmDanger (opts) {
  return openConfirm(opts, 'danger')
}

/**
 * D 类：结构化表单弹窗（审计 §3.6 / §7.7 的唯一出口）。一次提交返回一个对象，
 * 取代「串行 prompt」与「prompt 换成一个字符串输入框」两种偷换。
 *
 * fields 每项：{ key, label, type, placeholder, required, maxlength, min, max, integer,
 *                minlength, rows, options, help, httpsOnly, requiredMessage, validate }
 *   type: text | textarea | number | url | password | select | readonly-secret
 *   - password           → 输入框恒 type=password（密码/验证码不得明文回显）
 *   - readonly-secret    → **不是输入框**：等宽只读展示 + 复制按钮（授权码等敏感值）
 *   - select             → 需给 options: [{ value, label }]
 *   - validate(v, all)   → 返回错误文案（'' = 通过），字段级即时校验，替代「prompt 后再 alert」
 *
 * @param {Object} opts { title, message, fields, okLabel, cancelLabel, persistent, danger, consequence, parent }
 *   @param {Function} [opts.onSubmit] 可选 async (values) => void|payload：在弹窗内提交
 *     （loading + 禁用防双击；抛错则弹窗不关并显示原因）。不传则校验通过即关窗并 resolve 值对象。
 *   @param {Boolean} [opts.danger] C∩D：破坏性 + 必须输入。确认键走危险色，且**必须**同时给 consequence
 *     （签名层强制，与 confirmDanger 同一条约束）。普通输入表单不要传。
 *   @param {String} [opts.consequence] 危险表单的「将会发生什么」，danger:true 时必填
 * @returns {Promise<Object|null>} 确认 → 值对象（onSubmit 有返回则用其返回），取消/关闭 → null
 */
export function showForm (opts = {}) {
  if (opts == null || typeof opts !== 'object') reject('只接受选项对象')
  const fields = Array.isArray(opts.fields) ? opts.fields : []
  if (fields.length === 0) {
    reject('showForm() 至少需要一个字段（单字段也走字段规格：label/校验/一次提交缺一不可）')
  }
  fields.forEach((f, i) => {
    if (!f || !f.key || !f.label) reject(`fields[${i}] 必须有 key 与 label`)
  })

  // C∩D：破坏性动作 + 必须输入（如「删除订单商品」要填删除原因）。危险语义不得因为
  // 「多了一个输入框」就退化成主色确认键——§五「红色不得滥用」的反面同样成立：真危险不许不红。
  // 与 confirmDanger 同一条签名级约束：说不清后果的危险表单，dev 期直接拒。
  const isDanger = opts.danger === true
  if (isDanger && (!opts.consequence || !String(opts.consequence).trim())) {
    reject('showForm({ danger: true }) 必须给出 consequence（"将会发生什么"）；说不清后果的危险操作请去掉 danger')
  }

  return new Promise(resolve => {
    const dialog = Dialog.create({
      component: FormDialog,
      parent: opts.parent,
      title: opts.title ? String(opts.title) : '',
      message: opts.message ? String(opts.message) : '',
      fields,
      danger: isDanger,
      consequence: opts.consequence ? String(opts.consequence) : '',
      okLabel: opts.okLabel ? String(opts.okLabel) : '确定',
      cancelLabel: opts.cancelLabel ? String(opts.cancelLabel) : '取消',
      persistent: opts.persistent === true,
      onSubmit: typeof opts.onSubmit === 'function' ? opts.onSubmit : null
    })
    dialog.onOk(payload => resolve(payload === undefined ? null : payload))
    dialog.onCancel(() => resolve(null))
  })
}

export default { showMessage, confirmAction, confirmDanger, showForm }

// dev 自测/调试入口（生产构建下 process.env.NODE_ENV === 'production'，整段被静态求值剔除）：
// Phase 1 已迁完 6 个文件，但**业务页面仍有覆盖不到的面**——原语的签名级守卫（危险确认缺 consequence、
// 普通确认误用 danger 红、danger 表单缺 consequence）、persistent 语义、readonly-secret 掩码/复制，
// 这些是**门面契约**而不是某个页面的行为，没有页面可点。
// 因此 static/test/order_native_dialog_e2e.js 直接驱动本钩子自测（S5–S9 段）。
// 不做任何业务用途，**不要删**：删了那批契约断言失去唯一驱动入口。
if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
  window.__platoDialogs = { showMessage, confirmAction, confirmDanger, showForm }
}
